/**
 * English is the default and only locale. The reference dashboard is Norwegian;
 * this application is not.
 *
 * Deliberately a plain typed catalogue rather than an i18n library: nothing here
 * yet needs pluralisation, locale-aware formatting or lazy locale loading, and
 * picking a library before those requirements exist means picking it on taste.
 * When a second locale is actually commissioned, replace this module — every
 * consumer goes through t(), so the swap is contained. See docs/i18n.md.
 */
export const en = {
  app: {
    name: 'Tollit AirFlow',
    shortName: 'AirFlow',
  },
  common: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    signedInAs: 'Signed in as',
    retry: 'Try again',
    loading: 'Loading…',
    backToSignIn: 'Back to sign in',
    role: 'Role',
    organisation: 'Organisation',
    comingSoon: 'This screen has not been built yet.',
    placeholderBody:
      'The route, the guard chain and the shell are in place. The screen itself belongs to a later slice.',
    openMenu: 'Open menu',
    mainNavigation: 'Main navigation',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
  },
  signIn: {
    title: 'Sign in to AirFlow',
    subtitle: 'Use the work account you were invited with.',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    submit: 'Sign in',
    forgotPassword: 'Forgot password?',
    errors: {
      invalidCredentials: 'Incorrect email or password.',
      tooManyAttempts: 'Too many attempts. Wait a while, or reset your password.',
      network: 'Could not reach the sign-in service. Check your connection and try again.',
      unavailable: 'Something went wrong. Try again.',
    },
  },
  mfa: {
    codeLabel: '6-digit code',
    verify: 'Verify',
    verifying: 'Verifying…',
    cancel: 'Cancel',
    challengeTitle: 'Two-step verification',
    challengeTotpBody: 'Enter the 6-digit code from your authenticator app.',
    challengeSmsBody: 'We sent a 6-digit code to {phone}.',
    challengeSmsBodyNoPhone: 'We sent a 6-digit code to your recovery phone.',
    challengeSmsSendPrompt: 'Get a 6-digit code by SMS to {phone}.',
    challengeSmsSendPromptNoPhone: 'Get a 6-digit code by SMS to your recovery phone.',
    useSmsInstead: 'Use SMS instead',
    useTotpInstead: 'Use your authenticator app instead',
    sendingSms: 'Sending code…',
    resendSms: 'Send a new code',
    backToPassword: 'Back to sign in',
    reauthTitle: 'Confirm it’s you',
    reauthBody: 'For your security, enter your password again to continue.',
    reauthPasswordLabel: 'Password',
    reauthSubmit: 'Continue',
    totpStepTitle: 'Set up your authenticator app',
    totpStepBody:
      'Scan this QR code with an authenticator app (such as Google Authenticator or Microsoft Authenticator), then enter the 6-digit code it shows.',
    totpQrLabel: 'QR code for your authenticator app',
    totpKeyLabel: 'Or enter this key manually',
    totpOpenInApp: 'On this phone? Open in your authenticator app',
    totpStarting: 'Preparing your authenticator setup…',
    totpRestarted:
      'Your sign-in is confirmed. Scan this new code — the one you added a moment ago won’t work, so remove it from the app.',
    totpDefaultLabel: 'Authenticator app',
    phoneStepTitle: 'Add a recovery phone',
    phoneStepBody:
      'If you lose your authenticator app, you can sign in with a code sent to this phone and set up a new one.',
    phoneLabel: 'Mobile number, with country code',
    phonePlaceholder: '+47 912 34 567',
    phoneHint: 'Start with + and your country code, for example +47.',
    sendCode: 'Send code',
    phoneCodeSent: 'We sent a 6-digit code to {phone}.',
    changeNumber: 'Use a different number',
    phoneDefaultLabel: 'Recovery phone',
    phoneRestarted: 'Your sign-in is confirmed. Send the code again to continue.',
    enrollTitle: 'Protect your account',
    enrollBody:
      'AirFlow now needs a second step when you sign in: a code from an authenticator app, with your phone as a backup.',
    enrollStepProgress: 'Step {step} of {total}',
    enrollFinishing: 'Finishing up…',
    enrollContinue: 'Continue',
    enrollSignOut: 'Sign out',
    replaceTitle: 'Set up a new authenticator',
    replaceBody:
      'You signed in with a code sent by SMS. SMS is only for recovery, so set up your authenticator app again before continuing. Your old authenticator will stop working.',
    replaceVoluntaryBody:
      'Set up your new authenticator app. Once it works, the old one stops working and you’ll sign in again.',
    replaceRemoveOldFirst: 'Remove the old authenticator first',
    replaceRemoveOldFirstBody:
      'Your account can’t hold another authenticator. Remove the old one, then sign in again with an SMS code to finish.',
    replacingOld: 'Removing your old authenticator…',
    securityTitle: 'Sign-in & security',
    securitySubtitle: 'The second step you use when you sign in to AirFlow.',
    securityMenuItem: 'Sign-in & security',
    securityAuthenticator: 'Authenticator app',
    securityRecoveryPhone: 'Recovery phone',
    securityNotSetUp: 'Not set up',
    securityInUse: 'In use',
    securitySetUp: 'Set up',
    securityReplace: 'Replace',
    securityAdd: 'Add',
    securityChange: 'Change',
    securityChangeNote: 'Changing this signs you out, so you sign in again with the new setup.',
    securitySaved: 'Saved.',
    signInReasons: {
      'second-factors-changed':
        'Your sign-in settings changed. Sign in again with your authenticator app.',
      'second-factor-enrolled': 'Two-step verification is set up. Sign in again to continue.',
    },
    errors: {
      invalidCode: 'That code didn’t work. Check it and try again.',
      tooManyAttempts: 'Too many attempts. Wait a while and try again.',
      invalidPhone: 'Enter a mobile number starting with + and your country code.',
      phoneInUse: 'That phone is already enrolled.',
      tooManyFactors: 'Your account has too many second factors. Remove one first.',
      emailNotVerified:
        'Your email address isn’t verified yet. Open the link in your invite email, then try again.',
      network: 'Could not reach the sign-in service. Check your connection and try again.',
      wrongPassword: 'Incorrect password.',
      unavailable: 'Something went wrong. Try again.',
    },
  },
  forgotPassword: {
    title: 'Reset your password',
    body: "Enter the email you sign in with, and we'll send you a reset link.",
    submit: 'Send reset link',
    confirmation: "If an account exists for that email, we've sent a link to reset the password.",
    checkSpam: "Don't see it? Check your spam folder.",
  },
  notProvisioned: {
    title: 'Access not provisioned',
    body: 'Your account signed in successfully, but it has not been granted access to the AirFlow dashboard — or that access has been withdrawn.',
    // No "try again" here on purpose. The sign-in is fine, so retrying it
    // succeeds and lands the user right back on this screen, forever.
    action: 'Contact your Tollit administrator to request access.',
  },
  notPermitted: {
    title: 'Not permitted',
    body: 'Your role does not have access to this area.',
    action: 'Go to your dashboard',
  },
  signedOut: {
    title: 'You are signed out',
    body: 'Your session has been cleared from this browser.',
  },
  authError: {
    title: 'Sign-in could not be completed',
    body: 'Something went wrong while establishing your session. If this keeps happening, contact your Tollit administrator.',
  },
  offline: {
    title: 'No connection',
    body: 'AirFlow needs a connection to sign you in and to load data.',
  },
  nav: {
    controlTower: 'Control tower',
    activeShipments: 'Active shipments',
    delivered: 'Delivered',
    deviations: 'Deviations',
    access: 'Access & sharing',
    readyForCollection: 'Ready for collection',
    collected: 'Collected',
    shipments: 'Shipments',
    bookingReport: 'Booking report',
    invoicing: 'Invoicing',
    supplierInvoices: 'Supplier invoices',
  },
  roles: {
    Tollit: 'Tollit administrator',
    Driver: 'Driver',
    GroundHandler: 'Ground handler',
    ReceivingTerminal: 'Receiving terminal',
    LastMileCustomerService: 'Last-mile customer service',
    SenderCustomer: 'Sender customer',
  },
  access: {
    subtitle: "Manage who can sign in to AirFlow and what they can do once they're in.",
    loading: 'Loading…',
    loadError: "Couldn't load users. Try again.",
    inviteUser: 'Invite user',
    searchPlaceholder: 'Search by name or email',
    roleFilterAll: 'All roles',
    statusFilterAll: 'All statuses',
    columnUser: 'User',
    columnRole: 'Role',
    columnOrganisation: 'Organisation',
    columnStatus: 'Status',
    columnDateAdded: 'Date added',
    columnActions: 'Actions',
    previousPage: 'Previous',
    nextPage: 'Next',
    statusActive: 'Active',
    statusInvited: 'Invited',
    statusInvitedHint: 'Not signed in yet',
    statusDeactivated: 'Deactivated',
    statusDeactivatedHint: 'May keep access briefly — cache TTL',
    resendInvite: 'Resend invite',
    resendInviteSuccess: 'Invite email resent',
    resendInviteError: "Couldn't resend the invite. Try again.",
    deactivateAccess: 'Deactivate access',
    reactivateAccess: 'Reactivate access',
    copyEmail: 'Copy email',
    copyEmailSuccess: 'Email copied',
    copyEmailError: "Couldn't copy the email",
    emptyState: 'No users match your filters.',
    inviteFormTitle: 'Invite a new user',
    inviteFormName: 'Display name',
    inviteFormEmail: 'Work email',
    inviteFormUserType: 'User type',
    inviteFormOrganisation: 'Organisation',
    // Shared by the invite form and the detail screen's edit form — both
    // populate their Organisation dropdown from the same endpoint.
    organisationLoading: 'Loading organisations…',
    organisationError: "Couldn't load organisations. Try again.",
    organisationNone: 'This user type has no organisation.',
    inviteFormSend: 'Send invite',
    inviteFormSending: 'Sending…',
    inviteFormCancel: 'Cancel',
    inviteFormError: "Couldn't send the invite. Try again.",
    backToUsers: 'Back to users',
    viewDetails: 'View details',
    detailEmail: 'Email',
    detailRole: 'Role',
    detailOrganisation: 'Organisation',
    detailStatus: 'Status',
    detailDateAdded: 'Date added',
    editUser: 'Edit user',
    editNameLabel: 'Display name',
    editUserTypeLabel: 'User type',
    editOrganisationLabel: 'Organisation',
    editEmailLocked:
      "Email can't be changed — invite the user again under the new address instead.",
    editError: "Couldn't save these changes. Try again.",
    saveChanges: 'Save changes',
    savingChanges: 'Saving…',
    cancelEdit: 'Cancel',
    suspendUser: 'Suspend user',
    reactivateUser: 'Reactivate user',
    suspendError: "Couldn't suspend this user. Try again.",
    reactivateError: "Couldn't reactivate this user. Try again.",
    deleteUser: 'Delete user',
    deleteUserConfirmBody: "This can't be undone. The user will lose access immediately.",
    deleteUserConfirmAction: 'Yes, delete',
    deleteUserCancel: 'Cancel',
    deleteError: "Couldn't delete this user. Try again.",
    resetSecondFactors: 'Reset second factors',
    resetSecondFactorsConfirmTitle: 'Reset second factors?',
    resetSecondFactorsConfirmBody:
      'This removes the user’s authenticator app and recovery phone and signs them out everywhere. They set up two-step verification again at their next sign-in. Only do this after confirming who is asking.',
    resetSecondFactorsConfirmAction: 'Yes, reset',
    resetSecondFactorsCancel: 'Cancel',
    resetSecondFactorsSuccess: 'Second factors reset',
    resetSecondFactorsError: "Couldn't reset second factors. Try again.",
    userNotFoundTitle: 'User not found',
    userNotFoundBody: "This user doesn't exist, or has already been deleted.",
  },
  shipmentList: {
    title: 'Shipments',
    subtitle: 'One row per AWB.',
    titleDelivered: 'Delivered',
    subtitleDelivered: 'Shipments that have completed pallet scanning, one row per AWB.',
    titleDeviations: 'Deviations',
    subtitleDeviations:
      "Shipments whose API received order count doesn't match the parcel count, one row per AWB.",
    searchPlaceholder: 'Search by AWB or customer',
    searchPlaceholderCustomer: 'Search by AWB or manifest no.',
    customerFilterAll: 'All customers',
    statusFilterAll: 'All statuses',
    statusFilterCount: '{count} statuses',
    statusFilterClear: 'Clear',
    dateFrom: 'ETA from',
    dateTo: 'ETA to',
    columnNo: 'No.',
    columnStatus: 'Status',
    columnCustomer: 'Customer',
    columnEta: 'ETA (Local Time)',
    columnAwb: 'AWB',
    columnPallets: 'Boxes',
    columnPalletScanned: 'Pallet Scanned',
    columnWeight: 'Weight (kg)',
    columnParcels: 'Parcels',
    columnSubmitted: 'API Received',
    columnNoa: 'NOA',
    columnNoaQuantity: 'NOA qty',
    columnCustomsCleared: 'Custom Clear Date',
    columnDriverCollection: 'Driver Collection',
    noaPending: 'Pending',
    emptyState: 'No shipments match your filters.',
    loadError: "Couldn't load shipments. Try again.",
    loading: 'Loading…',
    previousPage: 'Previous',
    nextPage: 'Next',
    viewDetails: 'View details',
  },
  shipmentStatus: {
    PreAlert: 'Pre-Alert',
    ApiReceived: 'API Received',
    CustomCleared: 'Custom Cleared',
    Noa: 'NOA',
    DriverCollected: 'Driver Collected',
    PalletScanned: 'Delivered',
  },
  packageStatus: {
    AwaitingRegistration: 'Awaiting registration',
    InvoiceRendered: 'Invoice rendered',
    DeclaredInfo: 'Info declared',
    DeclaredInvoice: 'Invoice declared',
    DeclarationSubmitted: 'API Declaration Received',
    DeclarationFeesReceived: 'Fees received',
    DeclarationReceiptReceived: 'Receipt received',
    DeclarationClassificationReceived: 'Classification received',
    DeclarationFeesPaid: 'Fees paid',
    Duplicate: 'Duplicate',
  },
  paymentState: {
    Paid: 'Paid',
    Pending: 'Pending',
    NeedPayment: 'Payment needed',
  },
  shipmentDetail: {
    backToShipments: 'Back to shipments',
    loading: 'Loading…',
    loadError: "Couldn't load this shipment. Try again.",
    shipmentNotFoundTitle: 'Shipment not found',
    shipmentNotFoundBody: "This AWB doesn't exist.",
    summaryTitle: 'Shipment summary',
    summaryCustomer: 'Customer',
    summaryAwb: 'AWB',
    summaryManifestNo: 'Manifest no.',
    summaryFlightNo: 'Flight no.',
    summaryStatus: 'Status',
    summaryEtd: 'ETD (Local Time)',
    summaryEta: 'ETA (Local Time)',
    summaryTotalNetWeight: 'Total net weight',
    summaryTotalBoxes: 'Total boxes',
    summaryPalletScanned: 'Pallet scanned',
    summaryNoOfParcels: 'No. of parcels',
    summarySubmittedOrder: 'Submitted order',
    summaryNoaDateTime: 'NOA date/time',
    summaryNoaQuantity: 'NOA quantity',
    summaryCustomsClearedAt: 'Customs cleared at',
    summaryDriverCollected: 'Driver collected',
    packagesTitle: 'Packages',
    packagesOrderNo: 'Order no.',
    packagesStatus: 'Status',
    packagesPayment: 'Payment',
    packagesColli: 'Colli',
    packagesWeight: 'Net / gross weight',
    packagesClearanceDate: 'Clearance date',
    packagesEmpty: 'No packages recorded for this AWB yet.',
    noasTitle: 'Notices of arrival',
    noasEmpty: 'No NOA has arrived for this AWB yet.',
    noasPart: 'Part',
    noasReceived: 'Received',
    noasPieces: 'Pieces',
    noasCollected: 'Collected',
    noasStatus: 'Status',
    noasDocument: 'Document',
    noasStatusReady: 'Ready',
    noasStatusCollected: 'Collected',
  },
  driverCollections: {
    titleReady: 'Ready for collection',
    titleCollected: 'Collected',
    titleReadyOverview: 'Ready for collection',
    titleCollectedOverview: 'Collected',
    subtitleReady: 'AWBs ready for collection at OSL.',
    subtitleCollected: 'What has been collected in the last 3 days.',
    subtitleReadyOverview: 'AWBs ready for collection across drivers.',
    subtitleCollectedOverview:
      'Driver collection confirmations across drivers, in the last 3 days.',
    collectedWindowHint: 'Collected shipments stay here for 3 days.',
    emptyReady: 'Nothing is ready for collection right now.',
    emptyReadyOverview: 'No AWBs are ready for collection right now.',
    emptyReadyFiltered: 'Nothing from this ground handler is ready for collection.',
    emptyReadyDateFiltered: 'Nothing was customs cleared on this date.',
    emptyCollected: 'Nothing has been collected in the last 3 days.',
    emptyCollectedFiltered: 'Nothing from this ground handler was collected in the last 3 days.',
    emptyCollectedDateFiltered: 'Nothing was customs cleared on this date.',
    handlerFilterLabel: 'Ground handler',
    handlerFilterAll: 'All ground handlers',
    clearFilter: 'Show all ground handlers',
    dateFilterLabel: 'Customs cleared date',
    clearDateFilter: 'Show all dates',
    handler: 'Handler',
    pickupAddress: 'Pick-up address',
    units: 'Units',
    ofLabel: 'of',
    partLabel: 'Part',
    noaReceivedLabel: 'NOA received',
    notCleared: 'Not cleared',
    customsClearedAtLabel: 'Custom cleared at',
    confirmCollection: 'Confirm collection',
    collectedBadge: 'Collected',
    deviationBadge: 'Deviation',
    delayedBadge: 'Delayed',
    correctedBadge: 'Corrected',
    correctedFrom: 'Corrected from',
    collectedPrefix: 'Collected',
    collectedBy: 'by',
    collectByLabel: 'Collect by',
    collectByOverdue: 'overdue',
    downloadNoa: 'Download NOA',
    downloadNoaError: "Couldn't download the NOA. Try again.",
    correctCollection: 'Correct',
    previousPage: 'Previous',
    nextPage: 'Next',
    loadingReady: 'Loading ready collections…',
    loadingCollected: 'Loading collected shipments…',
    loadErrorReady: "Couldn't load ready collections. Try again.",
    loadErrorCollected: "Couldn't load collected shipments. Try again.",
    retry: 'Try again',
  },
  collectionConfirm: {
    title: 'Confirm collection',
    description:
      'Record the actual quantity collected for this AWB. A correction afterwards is recorded as a separate event, never as an edit.',
    correctTitle: 'Correct collection',
    correctDescription:
      'Record a correction to the collected quantity. This is added as a new event — the original record is never changed.',
    readyLabel: 'Units expected',
    readyUnknown: 'Unknown',
    currentLabel: 'Currently recorded',
    actualLabel: 'Actual units collected',
    reasonLabel: 'Reason for correction',
    reasonPlaceholder: 'What changed, and why?',
    reasonRequired: 'A reason is required to save a correction.',
    deviationWarning:
      'This does not match the expected quantity. Confirming will log a collection deviation for Tollit and the relevant last-mile party.',
    overExpectedWarning:
      'This is more than the expected quantity. Allowed, but it will be recorded as a deviation.',
    sameQuantityError: 'Enter a different quantity — this is already what is recorded.',
    acknowledgement: 'I confirm I physically collected this quantity at the stated location.',
    recordedAs: 'Recorded as',
    cancel: 'Cancel',
    submit: 'Confirm collection',
    submitCorrection: 'Save correction',
    submitError: "Couldn't save this collection. Try again.",
    conflictError: 'This AWB has already changed — refresh and look again.',
  },
  collectionHistory: {
    toggle: 'History',
    eventCollected: 'Collected',
    eventCorrected: 'Corrected',
    deviationTag: 'Deviation',
    reasonLabel: 'Reason',
    loading: 'Loading history…',
    loadError: "Couldn't load history.",
    retry: 'Try again',
    empty: 'No events recorded yet.',
  },
  bookingReport: {
    title: 'Booking report',
    tabWeekly: 'Weekly',
    tabMonthly: 'Monthly',
    tabYearly: 'Yearly',
    tabWeekday: 'Weekday distribution',
    tabAwbPrefix: 'AWB prefix analysis',
    tabPortfolio: 'Portfolio overview',

    yearFilterLabel: 'Year',
    dateBasisLabel: 'Date basis',
    dateBasisEta: 'ETA',
    dateBasisCustomsCleared: 'Customs cleared',
    customerFilterLabel: 'Customer',
    customerFilterAll: 'All customers',

    recentBookingsTitle: 'Recent bookings',
    recentBookingsDaysLabel: 'Days',
    recentBookingsParcels: 'Parcels',
    recentBookingsAwbs: 'AWBs',
    recentBookingsWeightPerAwb: 'Weight / AWB',
    recentBookingsParcelsPerAwb: 'Parcels / AWB',

    download: 'Download',
    viewAsTable: 'View as table',
    viewAsChart: 'View as chart',

    volumeTitle: 'Booking volume',
    volumeSubtitle: 'Parcels per customer, over time.',
    customerDevelopmentTitle: 'Customer development',
    customerDevelopmentSubtitle: 'Parcels per customer, over time.',
    productDevelopmentTitle: 'Product development',
    productDevelopmentSubtitle: 'Parcels per product type, over time.',

    growthTitle: 'Growth winners & losers',
    growthToggleWeek: 'Week',
    growthToggleMonth: 'Month',
    growthIncreaseTitle: 'Biggest increase',
    growthDecreaseTitle: 'Biggest decrease',
    growthNoGrowth: 'No growth',
    growthNoDecline: 'No decline',
    growthVsPeriod: 'vs. {period}',

    weekdayVolumeTitle: 'Total volume per weekday',
    weekdayShareTitle: 'Share per weekday',
    weekdayMon: 'Monday',
    weekdayTue: 'Tuesday',
    weekdayWed: 'Wednesday',
    weekdayThu: 'Thursday',
    weekdayFri: 'Friday',
    weekdaySat: 'Saturday',
    weekdaySun: 'Sunday',

    awbPrefixTableTitle: 'AWB prefix analysis',
    awbPrefixColumnPrefix: 'Prefix',
    awbPrefixColumnAirline: 'Airline',
    awbPrefixColumnGroundHandler: 'Ground handler',
    metricToggleAwb: 'AWB',
    metricToggleParcels: 'Parcels',
    metricToggleKg: 'KG',
    groundHandlerTrendTitle: 'Ground handler trend',
    portfolioPerGroundHandlerTitle: 'Portfolio per ground handler',
    periodToggleWeek: 'Week',
    periodToggleMonth: 'Month',

    portfolioPerCustomerTitle: 'Portfolio per customer',
    portfolioPerProductTypeTitle: 'Portfolio per product type',
    portfolioToggleWholePeriod: 'Whole period',
    portfolioToggleWeek: 'Week',
    portfolioToggleMonth: 'Month',
    portfolioToggleYear: 'Year',

    columnPeriod: 'Period',
    columnCustomer: 'Customer',
    columnProductType: 'Product type',
    columnParcels: 'Parcels',
    columnAwbs: 'AWBs',
    columnShare: 'Share',
    columnTotal: 'Total',

    seriesOther: 'Other',
    seriesUnclassified: 'Unclassified',
    seriesUnknown: 'Unknown',
    seriesNoAwbPrefix: 'No AWB prefix',

    productVoec: 'VOEC',
    productBulk: 'Bulk',
    productDdp: 'DDP',
    productIoss: 'IOSS',
    productAnnet: 'Other',

    loading: 'Loading…',
    loadError: "Couldn't load the booking report. Try again.",
    retry: 'Try again',
    emptyState: 'No bookings for these filters.',
  },
  invoicing: {
    title: 'Invoicing',
    subtitle:
      'Generate the invoice basis and summary for one customer and a customs-cleared period, and manage the invoices already generated.',
    tabGenerate: 'Generate',
    tabInvoices: 'Invoices',

    periodLabel: 'Period',
    periodMonth: 'Month',
    periodDateRange: 'Date range',
    monthLabel: 'Customs cleared in',
    clearedFromLabel: 'Cleared from',
    clearedToLabel: 'Cleared to',
    rangeIncomplete: 'Pick both dates.',
    rangeReversed: 'The start date must be on or before the end date.',
    rangeTooLong: 'A date range can cover at most one year.',
    customerLabel: 'Customer',
    customerPlaceholder: 'Select a customer',
    customersLoadError: "Couldn't load the customers. Try again.",

    selectPrompt: 'Select a customer and a period to preview what will be invoiced.',
    loadError: "Couldn't load the shipments for this period. Try again.",
    retry: 'Try again',
    emptyState: 'No uninvoiced, customs-cleared shipments for this customer in this period.',

    totalShipments: 'Shipments selected',
    totalShipmentsOf: 'of {eligible} eligible',
    totalBlocked: 'Blocked',
    totalBlockedHint: "Can't be invoiced until fixed",
    totalSummary: 'Summary total',
    totalRowSum: 'Sum of row totals',
    menziesNote:
      'The summary total leaves out the 4PX Menzies re-bill, so it is lower than the sum of row totals. The invoice files have always worked this way.',

    blockingTitle: "Blocking — these shipments can't be invoiced",
    warningsTitle: 'Warnings',
    goToRow: 'Go to {awb}',
    issueMessages: {
      price_missing: 'A price is missing for one of its charges.',
      not_priced: 'The shipment has not been priced yet.',
      voec_orders_without_price: 'Has VOEC/IOSS orders, but the VOEC/IOSS price or sum is 0.',
      bulk_orders_without_price: 'Has bulk orders, but the bulk price or sum is 0.',
      food_orders_without_price: 'Has food/high value orders, but that price or sum is 0.',
      ground_handling_and_closing_t1_zero: 'Ground handling and Closing T1 are both 0.',
      order_split_mismatch: "The order split doesn't add up to the parcel count.",
      ddp_inputs_missing:
        'The customer has a DDP outlay rate, but no duty or VAT is entered for this shipment.',
    },
    issueUnknown: 'Unrecognised issue ({code}).',

    previewBasisTab: 'Invoice basis',
    previewSummaryTab: 'Summary',
    previewTableLabel: 'Invoice basis preview',
    summaryTableLabel: 'Invoice summary preview',
    selectAll: 'Select all shipments',
    selectRow: 'Include {awb}',
    blockedBadge: 'Blocked',
    overriddenBadge: 'Edited',
    overriddenHint: 'The billing inputs were entered by hand.',
    editBillingInputs: 'Edit billing inputs',
    rowActions: 'Actions',
    summaryEmpty: 'No service has a value in the selected shipments.',
    summaryNotApplicable: 'N/A',
    summaryFooter: 'Total',
    summaryColumns: {
      company: 'Company',
      subCustomer: 'Sub-customer',
      service: 'Service',
      value: 'Value',
    },
    /** The invoice basis file's own headers (backend plan §4.8.2), kept identical so the preview reads like the file. */
    basisColumns: {
      invoiceNo: 'InvoiceNO',
      company: 'Company',
      subCustomer: 'Sub-customer',
      awb: 'AWB',
      pallets: 'Pallets',
      weight: 'Weight',
      parcels: 'Parcels',
      customsCleared: 'Custom cleared',
      voecOrders: 'Rec. orders VOEC',
      bulkOrders: 'Rec. orders bulk',
      foodOrders: 'Rec. orders high/foodstuff',
      voecPrice: 'VOEC/IOSS',
      bulkPrice: 'Bulk',
      foodPrice: 'Food/High value',
      sumVoec: 'Sum VOEC/IOSS',
      sumBulk: 'Sum BULK',
      sumFood: 'Sum Food/highvalue',
      groundHandling: 'Ground handling',
      feeToll: 'Fee Toll',
      feeVat: 'Fee VAT',
      ddpOutlay: 'DDP outlay',
      tdoc: 'TDOC',
      export: 'Export',
      hsCodes: 'HScodes',
      cmr: 'CMR',
      closingT1: 'Closing T1',
      minShipmentFee: 'Min. shipment fee',
      othersDescription: 'Others description',
      othersTotal: 'Others total',
      total: 'Total',
    },

    billingTitle: 'Billing inputs',
    billingDescription:
      "The order split, duty and VAT for {awb}. Saving recalculates the shipment's charges.",
    billingParcels: 'Parcels on this shipment',
    billingVoecOrders: 'VOEC/IOSS orders',
    billingBulkOrders: 'Bulk orders',
    billingFoodOrders: 'Food / high value orders',
    billingDutyPaid: 'Duty paid (Fee Toll)',
    billingVatPaid: 'VAT paid (Fee VAT)',
    billingDefaultHint:
      "Leave every field empty, or reset to default, to bill all parcels in the shipment's product category.",
    billingSplitMismatch:
      'The order split adds up to {sum}, not the {parcels} parcels on this shipment. You can still save it.',
    billingInvalidOrders: 'Order counts must be whole numbers of 0 or more.',
    billingInvalidAmount: 'Duty and VAT must be 0 or more.',
    billingResetToDefault: 'Reset to default',
    billingSaveError: "Couldn't save the billing inputs. Try again.",
    billingAlreadyInvoiced:
      'This shipment was invoiced in the meantime, so its billing inputs can no longer be changed.',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',

    invoiceNumberLabel: 'Invoice number',
    invoiceNumberHint: 'Required, up to 50 characters. Shown in the InvoiceNO column.',
    invoiceNumberRequired: 'Enter the invoice number.',
    invoiceNumberTooLong: 'The invoice number can be at most 50 characters.',
    invoiceNumberAlreadyUsed: 'This invoice number is already used by another invoice.',
    generate: 'Generate invoice',
    generateNothingSelected: 'Select at least one shipment to invoice.',
    confirmTitle: 'Generate this invoice?',
    confirmDescription:
      'The selected shipments are linked to this invoice and their amounts are frozen. Voiding the invoice frees them again.',
    confirmCustomer: 'Customer',
    confirmPeriod: 'Customs cleared',
    confirmShipments: 'Shipments',
    confirmInvoiceNumber: 'Invoice number',
    confirmGenerate: 'Generate and download',
    generating: 'Generating…',
    createError: "Couldn't generate the invoice. Try again.",
    alreadyInvoiced:
      'Someone else invoiced some of these shipments in the meantime. The preview has been refreshed — check it and generate again.',
    createdNotice: 'Invoice {number} generated. The download has started.',
    createdDownloadFailed:
      'Invoice {number} was generated, but the download failed. Download it from the Invoices tab.',

    listCustomerAll: 'All customers',
    listSearchLabel: 'Invoice number',
    listSearchPlaceholder: 'Search invoice number',
    listShowVoided: 'Show voided',
    listTableLabel: 'Invoices',
    listColumns: {
      number: 'Invoice number',
      customer: 'Customer',
      period: 'Customs cleared',
      shipments: 'Shipments',
      rowTotalSum: 'Sum of row totals',
      created: 'Created',
      status: 'Status',
      actions: 'Actions',
    },
    createdBy: 'by {name}',
    statusActive: 'Active',
    statusVoided: 'Voided',
    voidedReason: 'Reason: {reason}',
    listLoadError: "Couldn't load the invoices. Try again.",
    listEmpty: 'No invoices match these filters.',
    previousPage: 'Previous',
    nextPage: 'Next',
    loading: 'Loading…',

    download: 'Download',
    downloading: 'Downloading…',
    downloadError: "Couldn't download the invoice. Try again.",

    void: 'Void',
    voidTitle: 'Void invoice {number}?',
    voidDescription:
      'Its shipments can be invoiced again. The invoice stays in the list, marked voided, and can still be downloaded.',
    voidReasonLabel: 'Reason',
    voidReasonPlaceholder: 'For example: wrong invoice number',
    voidReasonRequired: 'A reason is required.',
    voidReasonTooLong: 'The reason can be at most 500 characters.',
    voidConfirm: 'Void invoice',
    voiding: 'Voiding…',
    voidError: "Couldn't void the invoice. Try again.",
    voidAlreadyVoided: 'This invoice has already been voided.',
    voidedNotice: 'Invoice {number} voided. Its shipments can be invoiced again.',
  },
  supplierInvoices: {
    title: 'Supplier invoices',
    subtitle:
      "Upload a ground handler's invoice, review what the automatic checks flagged, and approve it so its charges are recorded as shipment costs.",
    upload: 'Upload invoice',
    loading: 'Loading…',
    retry: 'Try again',
    previousPage: 'Previous',
    nextPage: 'Next',
    cancel: 'Cancel',

    listSearchLabel: 'Invoice number',
    listSearchPlaceholder: 'Search invoice number',
    listHandlerLabel: 'Ground handler',
    listHandlerAll: 'All ground handlers',
    listStatusLabel: 'Status',
    listStatusAll: 'All statuses',
    listTableLabel: 'Supplier invoices',
    listColumns: {
      number: 'Invoice number',
      handler: 'Ground handler',
      invoiceDate: 'Invoice date',
      grossTotal: 'Total incl. VAT',
      status: 'Status',
      review: 'Review',
      uploaded: 'Uploaded',
    },
    listLoadError: "Couldn't load the supplier invoices. Try again.",
    listEmpty: 'No supplier invoices match these filters.',
    status: {
      InReview: 'In review',
      Approved: 'Approved',
    },
    badgeToReview: '{count} to review',
    badgeReady: 'Ready to approve',
    badgeDisputed: '{currency} {amount} disputed',
    byName: 'by {name}',
    unknownUser: 'a removed user',

    uploadTitle: 'Upload supplier invoice',
    uploadDescription:
      "The invoice is read and every line is checked against our shipments, tariffs, NOAs and collections. You'll go straight to the review.",
    uploadSupportedHint: 'Only Menzies is supported so far, and only the *_DETAILED.xlsx file.',
    uploadHandlerLabel: 'Invoicing ground handler',
    uploadHandlerPlaceholder: 'Select a ground handler',
    uploadHandlersLoadError: "Couldn't load the ground handlers. Close this and try again.",
    uploadFileLabel: 'Invoice file (.xlsx, at most 10 MB)',
    uploadSubmit: 'Upload and check',
    uploading: 'Uploading and checking…',
    uploadHandlerRequired: 'Select the ground handler that sent the invoice.',
    uploadFileRequired: 'Choose the invoice file.',
    uploadFileExtension: 'The file must be an .xlsx spreadsheet.',
    uploadFileEmpty: 'The file is empty.',
    uploadFileTooLarge: 'The file is larger than 10 MB.',
    uploadError: "Couldn't upload the invoice. Try again.",
    /** Fallbacks for an upload field error that arrives without a `detail` (guide, Upload). */
    uploadFieldErrors: {
      groundHandlerId: {
        required: 'Select the ground handler that sent the invoice.',
        invalid_value: "This ground handler's invoices can't be read yet.",
      },
      file: {
        required: 'Choose the invoice file. It must not be empty.',
        invalid_value:
          "The file couldn't be read as this handler's DETAILED invoice. Check that it's the right .xlsx file, in NOK, and at most 10 MB.",
        totals_mismatch: "The invoice's lines, VAT and grand total don't add up.",
        already_used:
          'This invoice number has already been uploaded. Discard that invoice first to upload a corrected file.',
      },
    },

    backToList: 'Back to supplier invoices',
    notFoundTitle: 'Invoice not found',
    notFoundBody: 'It may have been discarded, or the link is wrong.',
    loadError: "Couldn't load this invoice. Try again.",
    invoiceDateMeta: 'Invoice date {date}',
    dueDateMeta: 'due {date}',
    netTotal: 'Net total',
    vatTotal: 'VAT',
    grossTotal: 'Total incl. VAT',
    linesTotal: 'Lines',
    linesBreakdown: '{ok} OK · {info} info · {exceptions} exceptions',
    toReview: 'To decide',
    toReviewHint: 'Exceptions with no decision yet',
    disputed: 'Disputed',
    disputedHint: '{currency} {amount} excl. VAT',
    uploadedMeta: 'Uploaded {time} by {name}',
    checkedMeta: 'Last checked {time}',

    revalidate: 'Re-run checks',
    revalidating: 'Re-running…',
    revalidateHint:
      'Run every check again — for example after a late pre-alert, NOA or collection was recorded. Decisions are kept.',
    approve: 'Approve',
    approveBlockedHint: 'Exceptions to decide before approving: {count}',
    discard: 'Discard',
    readOnlyNotice:
      'Approved {time} by {name}. Matched lines that were not disputed are recorded as actual costs on their shipments. This invoice can no longer change.',

    filterLabel: 'Show',
    filters: {
      undecided: 'To decide ({count})',
      exceptions: 'Exceptions ({count})',
      all: 'All lines ({count})',
    },
    linesTableLabel: 'Invoice lines',
    linesEmpty: {
      undecided: 'Every exception has a decision.',
      exceptions: 'The checks flagged nothing on this invoice.',
      all: 'This invoice has no lines.',
    },
    lineColumns: {
      row: 'Row',
      awb: 'AWB',
      charge: 'Charge',
      amount: 'Amount',
      checks: 'Checks',
      decision: 'Decision',
    },
    notMatched: 'Not matched',
    openShipment: 'Open shipment {awb}',
    pieces: '{count} pcs',
    vatLine: 'VAT {amount}',
    expectedLine: 'expected {amount}',
    billedOn: 'billed on {weight}',
    storageDays: '{billed} d billed · {dwell} d waited',
    storageBilledOnly: '{billed} d billed',
    chargeKinds: {
      TerminalWeightFee: 'Terminal — weight fee',
      TerminalPerKgSurcharge: 'Terminal — per-kg surcharge',
      TerminalFixedFee: 'Terminal — fixed fee',
      Storage: 'Storage',
      Unknown: 'Unknown charge',
    },
    outcomes: {
      Ok: 'OK',
      Info: 'Info',
      Exception: 'Exception',
    },
    issueException: 'Needs a decision',
    issueInfo: 'For information',
    /** The guide's "What the checks mean" table, for the help tooltip next to each message. */
    issueHelp: {
      unmatched_awb: 'No pre-alert has this MAWB.',
      wrong_ground_handler: 'The pre-alert has another ground handler, or none.',
      unknown_charge: "The charge description isn't one we know.",
      duplicate_charge:
        'The same MAWB and charge (for storage: and flight and date) is on another invoice from this handler, or twice on this one.',
      vat_mismatch: "The line's VAT isn't the amount × the invoice's VAT rate (±0.01).",
      tariff_missing: 'No import fee schedule or storage daily fee is valid on the service date.',
      rate_deviation:
        "The terminal handling amount isn't what our import fee rules give at the billed weight (±0.05).",
      billed_weight_unknown:
        "There's no per-kg surcharge line for this MAWB, so the billed weight is unknown.",
      billed_weight_differs: "The billed weight isn't the weight printed on the AWB.",
      pieces_differ: "The printed pieces aren't the pre-alert's box count.",
      storage_days_not_whole:
        "The storage amount isn't a whole number of days at the daily fee. Menzies prints no day count, so days are amount ÷ daily fee.",
      storage_no_noa: 'The shipment has no NOA, so its waiting time is unknown.',
      storage_days_exceed_dwell:
        'More days billed than any part waited (NOA date to first collection date, both counted).',
      storage_not_collected:
        "More days billed than the collected parts waited, and some part isn't collected yet.",
      collected_late:
        'A part was collected more than 24 h after customs clearance, so the storage is likely ours to pay.',
    },

    decisions: {
      Accepted: 'Accepted',
      Disputed: 'Disputed',
    },
    decidedBy: '{name}, {time}',
    accept: 'Accept',
    acceptLine: 'Accept row {rowNo}',
    dispute: 'Dispute…',
    disputeLine: 'Dispute row {rowNo}',
    editNote: 'Edit note…',
    editNoteLine: 'Edit the dispute note on row {rowNo}',
    clear: 'Clear',
    clearLine: 'Clear the decision on row {rowNo}',
    noDecision: 'Undecided',

    disputeTitle: 'Dispute row {rowNo} — {awb}',
    disputeDescription:
      "A disputed line isn't recorded as a cost on its shipment when the invoice is approved. Say why, so the handler can be asked to correct it.",
    disputeNoteLabel: 'Reason',
    disputeNotePlaceholder: 'For example: collected on day 4',
    disputeNoteRequired: 'A reason is required to dispute a line.',
    disputeNoteTooLong: 'The reason can be at most 500 characters.',
    disputeConfirm: 'Dispute line',
    saving: 'Saving…',

    approveTitle: 'Approve invoice {number}?',
    approveDescription: "This can't be undone. The invoice becomes read-only.",
    approveRecorded: 'Lines to be recorded as actual costs on their shipments: {count}',
    approveDisputed: 'Disputed lines left out: {count} ({currency} {amount} excl. VAT)',
    approveConfirm: 'Approve invoice',
    approving: 'Approving…',

    discardTitle: 'Discard invoice {number}?',
    discardDescription:
      'It disappears from the list, its decisions are lost, and its invoice number can be uploaded again — for example with a corrected file.',
    discardConfirm: 'Discard invoice',
    discarding: 'Discarding…',
    discardedNotice: 'Invoice {number} discarded. Its number can be uploaded again.',

    revalidatedNotice: 'Checks re-run. Decisions already made are kept.',
    alreadyApproved:
      'This invoice was approved in the meantime, so it can no longer change. It has been reloaded.',
    undecidedExceptions: 'Some exceptions still need a decision.',
    goneNotice: 'This invoice no longer exists. It may have been discarded by someone else.',
    writeError: "Couldn't save that. Try again.",
  },
} as const;

export type Messages = typeof en;
